import {
  ReadRequest,
  Request,
  RequestConfiguration,
  WriteRequest,
} from './request';
import { DataResponse, Response, WriteResponse } from './response';
import { EntityFields } from './entity';
import { FieldOptions, FieldOptionsResponse } from './field-options';
import { AbEntry } from './ab-entry';
import { KeyAndDisplay } from './key-and-display';
import { EntryKey } from './entry-key';
import { ValueAndDisplay } from './value-and-display';
import { Lead } from './lead';
import { SecurityStatus } from './security-status';
import { Opportunity } from './opportunity';
import { Case } from './case';

export enum TaskFields {
  Key = EntityFields.Key,
  ActionPlanKey = 'ActionPlanKey',
  Creator = EntityFields.Creator,
  AssignedTo = 'AssignedTo',
  ModifiedBy = EntityFields.ModifiedBy,
  CompletedBy = 'CompletedBy',
  DateTime = 'DateTime',
  EffectiveDate = 'EffectiveDate',
  Alarm = 'Alarm',
  SnoozeUntil = 'SnoozeUntil',
  CreationDate = EntityFields.CreationDate,
  LastModifyDate = EntityFields.LastModifyDate,
  Priority = 'Priority',
  Completed = 'Completed',
  CompletionDate = 'CompletionDate',
  Activity = 'Activity',
  IconType = 'IconType',
  AbEntryKey = 'AbEntryKey',
  LeadKey = 'LeadKey',
  Lead = 'Lead',
  With = 'With',
  Result = 'Result',
  Category = 'Category',
  AbEntry = 'AbEntry',
  WorkflowKey = 'WorkflowKey',
  WorkflowStageKey = 'WorkflowStageKey',
  WorkflowTaskTemplateKey = 'WorkflowTaskTemplateKey',
  WorkflowInstanceKey = 'WorkflowInstanceKey',
  WithKey = EntityFields.WithKey,
  SecStatus = EntityFields.SecStatus,
  RecurrenceKey = 'RecurrenceKey',
  RecurrenceType = 'RecurrenceType',
  Recurrence = 'Recurrence',
}

export interface Task {
  [TaskFields.Key]: string;
  [TaskFields.ActionPlanKey]?: string | null;
  [TaskFields.Creator]: KeyAndDisplay;
  [TaskFields.AssignedTo]: KeyAndDisplay;
  [TaskFields.ModifiedBy]: KeyAndDisplay;
  [TaskFields.CompletedBy]: KeyAndDisplay;
  [TaskFields.DateTime]: ValueAndDisplay<string>;
  [TaskFields.EffectiveDate]: ValueAndDisplay<string>;
  [TaskFields.Alarm]?: ValueAndDisplay<string>;
  [TaskFields.SnoozeUntil]: Date | null;
  [TaskFields.CreationDate]: ValueAndDisplay<string>;
  [TaskFields.LastModifyDate]: ValueAndDisplay<string>;
  [TaskFields.Priority]: string;
  [TaskFields.Completed]: boolean;
  [TaskFields.CompletionDate]: ValueAndDisplay<string>;
  [TaskFields.Activity]: string;
  [TaskFields.IconType]: number;
  [TaskFields.AbEntryKey]: EntryKey;
  [TaskFields.LeadKey]: EntryKey;
  [TaskFields.Lead]: Lead;
  [TaskFields.With]: {
    Opportunity?: Opportunity;
    Case?: Case;
  };
  [TaskFields.Result]: string;
  [TaskFields.Category]: string | ValueAndDisplay<string>;
  [TaskFields.AbEntry]: AbEntry;
  [TaskFields.WorkflowKey]: string;
  [TaskFields.WorkflowStageKey]: string;
  [TaskFields.WorkflowTaskTemplateKey]: string;
  [TaskFields.WorkflowInstanceKey]: string;
  [TaskFields.WithKey]: EntryKey;
  [TaskFields.SecStatus]: SecurityStatus;
  [TaskFields.RecurrenceKey]: string | null;
  [TaskFields.RecurrenceType]: number | null;
  [TaskFields.Recurrence]: TaskRecurrence | null;
}

export interface TaskFieldOptions {
  [TaskFields.Category]: KeyAndDisplay;
  [TaskFields.Result]: KeyAndDisplay;
  [TaskFields.Priority]: KeyAndDisplay;
  [TaskFields.AssignedTo]: KeyAndDisplay;
}

export interface TaskReadRequest extends Request {
  Task: ReadRequest<Task>;
}

export interface TaskResponse extends Response {
  Task: DataResponse<Task>;
}

export const TaskReadDriver: RequestConfiguration = {
  Drivers: {
    ITaskSearcher: 'Maximizer.Model.Access.Sql.TaskSearcher',
  },
};

export interface TaskFieldOptionsRequest extends Request {
  Task: FieldOptions<TaskFieldOptions>;
}

export interface TaskFieldOptionsResponse extends Response {
  Task: FieldOptionsResponse<TaskFieldOptions>;
}

export interface TaskWriteRequest extends Request {
  Task: WriteRequest<Task> | WriteRequest<TaskCreate>;
}

export interface TaskWriteResponse extends Response {
  Task: WriteResponse<Task>;
}

export interface TaskCreate {
  Key: string | null;
  Activity: string;
  Datetime: Date;
  Alarm: Date | null;
  Priority: string | null;
  Category?: string | null;
  Result?: string | null;
  AssignedTo: {
    UID: string;
  };
  AbEntryKey: string | null;
  LeadKey: string | null;
  WithKey?: string | null;
}

export type RecurrencePatternType =
  | 'daily'
  | 'weekly'
  | 'absoluteMonthly'
  | 'absoluteYearly';

export type DayOfWeek =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export interface RecurrencePattern {
  Type: RecurrencePatternType;
  Interval: number;
  Regenerating: boolean;
  DaysOfWeek?: DayOfWeek[];
  FirstDayOfWeek?: DayOfWeek;
  DayOfMonth?: number;
  Month?: number;
  Index?: string;
}

export interface RecurrenceRange {
  StartDate: string;
  EndDate?: string;
  Occurrences?: number;
  RecurrenceTimeZone?: string;
}

export interface TaskRecurrence {
  Key?: string | null;
  Pattern: RecurrencePattern;
  Range: RecurrenceRange;
}
